import { z } from 'zod';
import { Instant, LocalDate, MinorAmount, PageQuery, Phone, TenantSlug, TimeZone, Uuid, problemDetails, } from './common.js';
import { AppointmentBranchView, AppointmentEmployeeView, AppointmentServiceView, AppointmentStatus, BookingSource, CancellationActor, } from './booking.js';
import { PROBLEM_TYPE_PREFIX } from './errors.js';
// Контракт ПУБЛИЧНОГО API для сторонних разработчиков (ADR-036).
//
// Этот файл — единственный источник истины: из него валидируется вход и из него же
// генерируется openapi.json и документация developers.planerius.ru (ADR-036 §8.1).
// Второго описания этих тел не существует и появиться не должно: расхождение
// документации с поведением — самый дорогой дефект публичного API, потому что
// обнаруживается он в проде у чужого салона, а виноваты в нём мы.
//
// Схемы событий вебхуков лежат в ./public-api-webhooks.ts и переиспользуют
// представления записи из ./booking.ts — иначе тело вебхука разъедется с ответом API.
// ───────────────────────── версия и служебные заголовки ─────────────────────────
/** Мажорная версия в пути. Дата-версий (модель Stripe) нет осознанно — ADR-036 §3. */
export const PUBLIC_API_VERSION = 'v1';
/** Общий префикс публичных маршрутов. Витринные /v1/* к публичному API не относятся. */
export const PUBLIC_API_BASE_PATH = '/v1/public-api';
/**
 * Имена заголовков ответа. Вынесены константами, а не строками по коду:
 * заголовки публичного API — часть контракта, и опечатка в одном месте
 * тихо лишает интегратора лимитов или предупреждения о sunset.
 */
export const PUBLIC_API_HEADERS = {
    /** Сквозной идентификатор запроса. Без него поддержка слепа — отдаём всегда (ADR-036 §9). */
    requestId: 'x-request-id',
    /** Мажорная версия, обработавшая запрос: интегратор должен видеть её, не разбирая URL. */
    apiVersion: 'Planerius-Api-Version',
    /** Политика лимитов целиком: draft-ietf-httpapi-ratelimit-headers. */
    rateLimitPolicy: 'RateLimit-Policy',
    rateLimitLimit: 'RateLimit-Limit',
    rateLimitRemaining: 'RateLimit-Remaining',
    rateLimitReset: 'RateLimit-Reset',
    /** Ответ отдан из снимка идемпотентности, а не выполнен заново (ADR-036 §6). */
    idempotentReplay: 'Idempotent-Replay',
    /**
     * Появляется на КАЖДОМ ответе в последние 14 дней жизни ключа.
     * Письма читает бухгалтер салона, заголовки — код интегратора; работает только второе.
     */
    keyExpires: 'Planerius-Key-Expires',
    /** RFC 9745: момент объявления версии устаревшей. */
    deprecation: 'Deprecation',
    /** RFC 8594: момент отключения версии. Объявляется сразу, а не «ближе к делу». */
    sunset: 'Sunset',
    /** Ссылка на гид миграции: rel="deprecation". */
    link: 'Link',
    /** Чего именно не хватило: Bearer error="insufficient_scope", scope="clients.pii". */
    wwwAuthenticate: 'WWW-Authenticate',
};
/** Обязательный заголовок идемпотентности на всех POST/PATCH/DELETE (ADR-036 §6). */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';
/**
 * Значение Idempotency-Key публичного API: 16–255 символов.
 * Нижняя граница выше витринной (там 8): у машины нет оправдания короткому ключу,
 * а совпадение двух коротких «order-1» из разных интеграций — тихая выдача чужого ответа.
 */
export const PublicApiIdempotencyKey = z
    .string()
    .min(16)
    .max(255)
    .regex(/^[A-Za-z0-9._:-]+$/, 'допустимы латиница, цифры и . _ : -');
/**
 * Разобранные лимитные заголовки для типизированного клиента @planerius/api-client:
 * интегратор, который видит остаток в типах, замедляется сам и не доводит до 429.
 */
export const RateLimitSnapshot = z.object({
    limit: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
    /** Секунд до пополнения ведра, а не абсолютный момент: часы интегратора могут врать. */
    resetSeconds: z.number().int().nonnegative(),
});
/** Вес запроса в токенах ведра. Не все запросы равны — ADR-036 §4.2. */
export const REQUEST_WEIGHT = {
    /** Обычное чтение и запись. */
    default: 1,
    /** Списочная выдача с limit > 50. */
    largePage: 2,
    /** Расчёт слотов — самый дорогой запрос продукта (ADR-031). */
    slotsRead: 5,
    /** Аналитические агрегаты. */
    analyticsRead: 10,
};
// ───────────────────────── формат ключа ─────────────────────────
/**
 * Окружение ключа. Различается ДО обращения к базе, по префиксу строки:
 * это наносекунды проверки против самой дорогой ошибки интегратора —
 * «перепутал ключи в конфиге и разослал тестовые уведомления живой базе» (ADR-036 §7.2).
 */
export const ApiKeyEnvironment = z.enum(['live', 'test']);
/**
 * Регулярное выражение ключа. ПУБЛИКУЕТСЯ на developers.planerius.ru и в SECURITY.txt
 * и подаётся в программы сканирования секретов: фиксированный узнаваемый префикс —
 * единственный способ, которым GitHub secret scanning и gitleaks отличают наш ключ
 * от случайной строки. Менять формат нельзя никогда: смена ломает сканеры утечек.
 */
export const API_KEY_PATTERN = /^plnr_(live|test)_[0-9a-f]{8}_[0-9A-Za-z]{32}$/;
/** Полная строка ключа. Существует в двух местах: в ответе на выпуск и в конфиге интегратора. */
export const ApiKeyString = z
    .string()
    .regex(API_KEY_PATTERN, 'ожидается ключ вида plnr_live_<kid>_<secret>');
/**
 * kid — публичный идентификатор ключа, 8 hex. По нему ищется строка в базе.
 * Поиск по kid, а не перебором хешей: иначе смена pepper инвалидировала бы все ключи разом.
 */
export const ApiKeyId = z.string().regex(/^[0-9a-f]{8}$/, 'ожидается 8 hex-символов');
/**
 * Заголовок передачи ключа ровно один. X-Api-Key не поддерживается: authorization
 * уже стоит в redact-списке pino, а каждый дополнительный заголовок — ещё одно место,
 * где секрет утечёт в лог, в логи nginx и в скриншот от интегратора.
 */
export const API_KEY_HEADER = 'authorization';
export const API_KEY_SCHEME = 'Bearer';
// ───────────────────────── скоупы ─────────────────────────
/**
 * Закрытый перечень скоупов. Расширяется ТОЛЬКО через новый ADR.
 *
 * Скоуп — не удобная группировка эндпоинтов, а граница правовой ответственности:
 * разработчик с телефоном клиента и разработчик со временем визита — это два разных
 * объёма поручения и два разных разговора с РКН (ADR-036 §2.1).
 */
export const PublicApiScope = z.enum([
    'catalog.read',
    'catalog.write',
    'schedule.read',
    'schedule.write',
    // Вынесен из catalog.read отдельно: у расчёта слотов свой вес 5 в лимитах (ADR-031).
    'slots.read',
    'appointments.read',
    'appointments.write',
    'clients.read',
    'clients.write',
    // Снимает маску с контактов. Только вместе с clients.read. Главный рычаг минимизации
    // ПД, который у нас вообще есть: большинству интеграций телефон не нужен вовсе.
    'clients.pii',
    'analytics.read',
    'webhooks.manage',
]);
/**
 * Человеческие формулировки для раздела «Кому открыт доступ к вашим данным» и для
 * будущего consent screen. Владелец салона принимает решение по этому тексту,
 * а не по строке appointments.write — иначе акцепт поручения бессмысленен.
 */
export const SCOPE_TITLES = {
    'catalog.read': 'Читать услуги, цены, мастеров и филиалы',
    'catalog.write': 'Создавать и править услуги, категории и привязки мастеров',
    'schedule.read': 'Читать графики смен и занятость мастеров',
    'schedule.write': 'Ставить смены и блокировать время',
    'slots.read': 'Получать свободное время для записи',
    'appointments.read': 'Читать записи: время, услуга, мастер, статус, сумма',
    'appointments.write': 'Создавать, переносить и отменять записи',
    'clients.read': 'Читать карточки клиентов со скрытыми контактами',
    'clients.write': 'Создавать и править карточки клиентов',
    'clients.pii': 'Видеть телефон, e-mail и дату рождения клиентов открытым текстом',
    'analytics.read': 'Читать сводную статистику вашего салона',
    'webhooks.manage': 'Управлять своими подписками на события',
};
/**
 * Скоупы, дающие доступ к персональным данным. Выпуск ключа с любым из них делает
 * ТОЛЬКО роль owner, с переподтверждением 2FA и после акцепта поручения конкретному
 * разработчику (ADR-036 §2.5 п.2): в appointments.* лежит имя и время визита
 * конкретного человека, это ПД, а не «просто расписание».
 */
export const PII_SCOPES = [
    'appointments.read',
    'appointments.write',
    'clients.read',
    'clients.write',
    'clients.pii',
];
/**
 * Скоупов не будет никогда — записано здесь, чтобы через год не обсуждать заново
 * (ADR-036 §2.1 и §9). Значения намеренно обычные строки, а не PublicApiScope:
 * в перечень они не должны попасть даже опечаткой.
 */
export const NEVER_GRANTED_SCOPES = [
    'billing.*',
    'users.*',
    'audit.read',
    'channels.*',
    'consents.write',
];
// ───────────────────────── приложение разработчика ─────────────────────────
/**
 * private — собственная интеграция салона (ключ создаёт сам салон в кабинете);
 * public — приложение будущего каталога (этап 5, гранты через OAuth-консент).
 * Различие введено сразу, чтобы добавление OAuth не потребовало миграции данных.
 */
export const ApiApplicationMode = z.enum(['private', 'public']);
/**
 * Состояние модерации приложения. Значения повторяют перечисление БД
 * `api_application_status` ОДИН В ОДИН и в том же порядке: контракт печатается
 * в openapi.json, а код (`ApiKeyGuard`, `ApiKeysService`) сверяет строку со значением
 * из базы. Любое расхождение здесь означает документацию, по которой чужой код
 * никогда не сойдётся с поведением: ключи выпускает только `approved`, а такого
 * значения в контракте до этого не существовало вовсе.
 *
 * `suspended` — приложение временно отключено нами (инцидент, нарушение условий):
 * ключи остаются в базе и отвечают 403, чтобы отключение было обратимым.
 */
export const ApiApplicationStatus = z.enum([
    'draft',
    'pending_review',
    'approved',
    'suspended',
    'rejected',
    'archived',
]);
/**
 * Реквизиты разработчика. Обязательны, а не желательны: без ИНН и ответственного
 * за обработку ПД салон не может оформить поручение третьему лицу по ч. 3 ст. 6 152-ФЗ,
 * то есть выпуск ключа становится невозможен юридически.
 */
export const DeveloperDetails = z.object({
    legalName: z.string().trim().min(2).max(300),
    inn: z.string().regex(/^(\d{10}|\d{12})$/, 'ожидается ИНН из 10 или 12 цифр'),
    /** Кому салон и мы пишем при инциденте: ответ обязан быть в течение 12 часов. */
    securityEmail: z.email(),
    /** Технический контакт: сюда уходят письма об истечении ключа и о sunset версии. */
    technicalEmail: z.email(),
    technicalPhone: Phone.optional(),
    /** Ответственный за организацию обработки ПД (ст. 22.1 152-ФЗ) на стороне разработчика. */
    dataProtectionOfficer: z.string().trim().min(2).max(200),
});
/**
 * Акцепт «Условий использования API Planerius» разработчиком. Версия и хеш текста —
 * та же механика, что у client_consents: через год иначе не доказать, на что согласились.
 */
export const DeveloperTermsAcceptance = z.object({
    version: z.string().min(1).max(64),
    textHash: z.string().regex(/^[0-9a-f]{64}$/, 'ожидается sha256 текста в hex'),
    acceptedAt: Instant,
});
export const CreateApiApplicationRequest = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(2000).optional(),
    homepageUrl: z.url().optional(),
    mode: ApiApplicationMode.default('private'),
    developer: DeveloperDetails,
    /**
     * Максимум того, что приложение вправе когда-либо запросить. Эффективные права —
     * пересечение с грантом салона и с тарифом: deny-by-default на трёх уровнях сразу.
     */
    requestedScopes: z.array(PublicApiScope).min(1),
    /** Ровно true: без акцепта условий приложение не создаётся. */
    acceptTerms: z.literal(true),
    termsVersion: z.string().min(1).max(64),
});
export const UpdateApiApplicationRequest = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    homepageUrl: z.url().nullable().optional(),
    developer: DeveloperDetails.partial().optional(),
});
export const ApiApplicationView = z.object({
    id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    homepageUrl: z.url().nullable(),
    mode: ApiApplicationMode,
    status: ApiApplicationStatus,
    developer: DeveloperDetails,
    allowedScopes: z.array(PublicApiScope),
    terms: DeveloperTermsAcceptance,
    createdAt: Instant,
});
// ───────────────────────── грант: доступ приложения к салону ─────────────────────────
/**
 * Поручение обработки третьему лицу, принятое владельцем салона.
 *
 * Текст поручения генерируется НАМИ из состава скоупов: салон не должен писать
 * юридический документ сам, а мы не вправе дать доступ к ПД без документа —
 * техническую возможность передачи создаём мы, и без акцепта мы своими руками
 * делаем салон нарушителем 152-ФЗ (ADR-036 §2.5).
 */
export const ThirdPartyProcessingAcceptance = z.object({
    documentVersion: z.string().min(1).max(64),
    textHash: z.string().regex(/^[0-9a-f]{64}$/, 'ожидается sha256 текста в hex'),
    acceptedAt: Instant,
    /** Кто именно принял. Только владелец салона: не manager и не admin. */
    acceptedByUserId: Uuid,
    /** Состав скоупов на момент акцепта: расширение скоупов требует НОВОГО акцепта. */
    scopes: z.array(PublicApiScope).min(1),
    /** Срок, на который салон поручает обработку. Бессрочных поручений не бывает. */
    validUntil: Instant.nullable(),
    revokedAt: Instant.nullable(),
});
/**
 * Создание гранта. Сегодня его создаёт кабинет при выпуске ключа, завтра — OAuth-консент.
 * Ключ — способ предъявить грант, а не сам грант: именно это разделение позволяет
 * добавить OAuth в этапе 5 без миграции данных.
 */
export const CreateApiGrantRequest = z.object({
    applicationId: Uuid,
    scopes: z.array(PublicApiScope).min(1),
    /**
     * Пустой массив = все филиалы салона, включая будущие. Непустой — жёсткий фильтр
     * в репозитории И проверка на выходе: сеть из 20 точек обязана мочь пустить
     * подрядчика ровно в одну.
     */
    branchIds: z.array(Uuid).default([]),
    /** Ровно true; при скоупах из PII_SCOPES сервер дополнительно требует 2FA владельца. */
    acceptProcessingInstruction: z.literal(true),
    processingInstructionVersion: z.string().min(1).max(64),
});
export const ApiGrantView = z.object({
    id: Uuid,
    applicationId: Uuid,
    applicationName: z.string(),
    scopes: z.array(PublicApiScope),
    branchIds: z.array(Uuid),
    acceptance: ThirdPartyProcessingAcceptance,
    createdAt: Instant,
    revokedAt: Instant.nullable(),
    /**
     * Сколько карточек клиентов приложение выгрузило за 30 дней. Нужно не для красоты:
     * это готовый ответ салона проверяющему и основа для «planerius incident scope» —
     * при утечке у разработчика объём затронутых ПД надо назвать за 24 часа,
     * а задним числом по логам он не считается (ADR-036 §2.5 п.3–4).
     */
    piiReads30d: z.number().int().nonnegative(),
});
// ───────────────────────── ключи ─────────────────────────
/** CIDR IPv4/IPv6. Полная нормализация — на сервере, схема отсекает явный мусор. */
export const Cidr = z
    .string()
    .regex(/^(?:(?:\d{1,3}\.){3}\d{1,3}\/(?:3[0-2]|[12]?\d)|[0-9A-Fa-f:]+\/(?:12[0-8]|1[01]\d|\d{1,2}))$/, 'ожидается CIDR вида 203.0.113.0/24 или 2001:db8::/32');
/**
 * Режим подписи ВХОДЯЩИХ запросов. signed — опция, не обязанность (ADR-036 §1.5):
 * TLS 1.3 закрывает перехват, а обязательная подпись переложила бы на каждого
 * интегратора канонизацию тела, в которой половина ошибётся.
 */
export const ApiKeySignatureMode = z.enum(['none', 'signed']);
export const IssueApiKeyRequest = z
    .object({
    grantId: Uuid,
    name: z.string().trim().min(1).max(120),
    environment: ApiKeyEnvironment.default('live'),
    /**
     * Срок ОБЯЗАТЕЛЕН. Бессрочных ключей нет: ключ без срока живёт вечно в чужом git,
     * в чужом чате поддержки и в наследстве уволившегося интегратора.
     */
    expiresInDays: z.number().int().min(1).max(365).default(90),
    /** Подмножество скоупов гранта. Пусто = весь грант. */
    scopes: z.array(PublicApiScope).default([]),
    /** Пустой список = без ограничения по адресу. */
    allowedCidrs: z.array(Cidr).max(32).default([]),
    signatureMode: ApiKeySignatureMode.default('none'),
    /**
     * Осознанный отказ от allowlist при выдаче clients.pii: «интеграция работает
     * с динамического адреса, я принимаю риск». Пишется в audit_log и в журнал
     * третьих лиц. Allowlist — единственная мера, которая продолжает работать
     * ПОСЛЕ утечки ключа, поэтому отказ от неё обязан быть записанным решением,
     * а не молчанием формы.
     */
    acceptDynamicIpRisk: z.boolean().default(false),
})
    .refine((r) => !r.scopes.includes('clients.pii') || r.scopes.includes('clients.read'), {
    error: 'clients.pii выдаётся только вместе с clients.read',
    path: ['scopes'],
})
    .refine((r) => !r.scopes.includes('clients.pii') || r.allowedCidrs.length > 0 || r.acceptDynamicIpRisk, {
    error: 'для clients.pii нужен список адресов либо явный акцепт риска владельцем',
    path: ['allowedCidrs'],
});
export const ApiKeyStatus = z.enum(['active', 'expiring', 'expired', 'revoked']);
/**
 * Ключ в списке. Секрета здесь нет и быть не может: в базе лежит только
 * HMAC-SHA-256(pepper, secret), восстановить исходную строку нельзя.
 */
export const ApiKeyView = z.object({
    kid: ApiKeyId,
    name: z.string(),
    environment: ApiKeyEnvironment,
    /** Отображаемая форма «plnr_live_7f3a9c21_…». Достаточно, чтобы узнать ключ в конфиге. */
    displayPrefix: z.string(),
    last4: z.string().length(4),
    status: ApiKeyStatus,
    scopes: z.array(PublicApiScope),
    branchIds: z.array(Uuid),
    allowedCidrs: z.array(Cidr),
    signatureMode: ApiKeySignatureMode,
    createdAt: Instant,
    expiresAt: Instant,
    /** По моменту последнего использования салон видит мёртвые ключи и сносит их. */
    lastUsedAt: Instant.nullable(),
    revokedAt: Instant.nullable(),
    revokedReason: z.string().nullable(),
});
/**
 * Ответ на выпуск и на ротацию — ЕДИНСТВЕННОЕ место, где существует секрет.
 * «Покажите ещё раз» = выпустить новый: то же правило, что для токена бота (ADR-025).
 */
export const IssuedApiKey = z.object({
    key: ApiKeyView,
    /** Полная строка ключа. Не логируется, не кешируется и не возвращается больше никогда. */
    secret: ApiKeyString,
    /**
     * Машинный признак «это последний раз». Нужен кабинету и типизированному клиенту,
     * чтобы показать предупреждение, а не спрятать секрет в свёрнутый блок.
     */
    secretShownOnce: z.literal(true),
});
/**
 * Ротация с перекрытием: выпускается второй ключ того же гранта, старому ставится
 * expiresAt = now() + 72 ч. Семьдесят два часа — это выходные плюс запас:
 * интегратор успевает выкатить конфиг, не роняя салон.
 */
export const RotateApiKeyRequest = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    /** Срок нового ключа; по умолчанию — как у ротируемого. */
    expiresInDays: z.number().int().min(1).max(365).optional(),
});
export const RotateApiKeyResponse = IssuedApiKey.extend({
    previousKid: ApiKeyId,
    /** Когда перестанет работать старый ключ. Показывается интегратору крупно. */
    previousKeyExpiresAt: Instant,
});
/** Причина отзыва. leaked означает немедленный отзыв без согласования с салоном. */
export const ApiKeyRevokeReason = z.enum([
    'leaked',
    'rotated',
    'unused',
    'integration_removed',
    'other',
]);
export const RevokeApiKeyRequest = z.object({
    reason: ApiKeyRevokeReason,
    comment: z.string().trim().max(500).optional(),
});
export const ListApiKeysQuery = PageQuery.extend({
    applicationId: Uuid.optional(),
    environment: ApiKeyEnvironment.optional(),
    status: ApiKeyStatus.optional(),
});
export const ApiKeyList = z.object({
    items: z.array(ApiKeyView),
    nextCursor: z.string().nullable(),
});
// ───────────────── кабинет: своя интеграция салона (4-В6, 4-В7) ─────────────────
//
// Решение владельца 11.10.2026: сейчас только СВОЯ интеграция салона (kind = private) —
// касса, CRM, подрядчик, которого салон заводит сам. Каталога сторонних приложений
// и OAuth-консента пока нет. Ключи выпускает тот, у кого право api_keys.manage; доступ
// к данным клиентов (PII_SCOPES) — только владелец со свежим вторым фактором и только
// с акцептом поручения, текст которого составляем мы.
/**
 * Версия «Условий использования API» и поручения. Оба текста — НАШ ЧЕРНОВИК (решение
 * владельца: юрист заменит). Версия в имени говорит об этом прямо: акцепт черновика
 * должен отличаться в журнале от акцепта утверждённого текста.
 */
export const API_TERMS_VERSION = 'draft-2026-10';
export const PROCESSING_INSTRUCTION_VERSION = 'draft-2026-10';
/**
 * Условия использования API для своей интеграции салона. Короткие и исполнимые:
 * за каждым пунктом стоит механизм, а не обещание.
 */
export const API_TERMS_TEXT = [
    'Условия использования API Planerius для собственной интеграции салона (черновик, ' +
        `редакция ${API_TERMS_VERSION}).`,
    '1. Ключ доступа — секрет салона. Его передают только подрядчику, указанному в реквизитах ' +
        'интеграции, и не публикуют в открытых репозиториях и переписке.',
    '2. Утёкший ключ салон отзывает сам в разделе «Разработчикам»; Planerius отзывает его ' +
        'без согласования, если увидит в открытом доступе.',
    '3. Доступ к данным клиентов открывается только после акцепта поручения на обработку ' +
        'персональных данных владельцем салона со вторым фактором.',
    '4. Planerius ограничивает частоту запросов и объём выдачи и ведёт журнал обращений ' +
        'интеграции, доступный владельцу салона.',
    '5. Заметки о клиентах, фотографии работ, платёжные данные и журнал действий через API ' +
        'не выдаются никогда.',
].join('\n');
/** Своя интеграция салона: кто подрядчик и зачем он нужен. */
export const CreateIntegrationRequest = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(2000).optional(),
    homepageUrl: z
        .url()
        .max(2048)
        .refine((url) => url.startsWith('https://'), 'адрес сайта — только https://')
        .optional(),
    developer: DeveloperDetails,
    /** Ровно true: без акцепта условий интеграция не заводится. */
    acceptTerms: z.literal(true),
    termsVersion: z.literal(API_TERMS_VERSION),
});
/**
 * Черновик доступа — из него составляется текст поручения. Те же поля приходят и при
 * выдаче: сервер собирает текст заново и сверяет хеш с тем, который видел владелец.
 */
export const IntegrationAccessDraft = z.object({
    scopes: z.array(PublicApiScope).min(1),
    /** Пусто — все филиалы салона, включая будущие. */
    branchIds: z.array(Uuid).max(200).default([]),
    /** Срок доступа, поручения и ключа. Бессрочных нет; «год» — максимум. */
    expiresInDays: z.number().int().min(1).max(365).default(90),
    /** Цель обработки словами салона: «синхронизация записей с кассой». Идёт в поручение. */
    purpose: z.string().trim().min(3).max(300),
});
/** Текст поручения ровно таким, каким его увидит и примет владелец. */
export const ProcessingInstructionView = z.object({
    version: z.string(),
    text: z.string(),
    /** sha256 текста в hex: его владелец и акцептует. */
    textHash: z.string().regex(/^[0-9a-f]{64}$/),
    /** false — в правах нет персональных данных, поручение не нужно, документ справочный. */
    required: z.boolean(),
    validUntil: Instant,
});
/**
 * «Акцептовать и выпустить ключ» (4-В7): доступ, акцепт поручения и первый ключ —
 * одним действием и одной транзакцией. Частичного исхода («доступ выдан, ключа нет»)
 * быть не должно: такой доступ некому показать и нечем отозвать.
 */
export const CreateIntegrationAccessRequest = IntegrationAccessDraft.extend({
    keyName: z.string().trim().min(1).max(120),
    environment: ApiKeyEnvironment.default('live'),
    allowedCidrs: z.array(Cidr).max(32).default([]),
    acceptDynamicIpRisk: z.boolean().default(false),
    signatureMode: ApiKeySignatureMode.default('none'),
    /** Обязателен, когда в правах есть данные клиентов; без них — null. */
    instruction: z
        .object({
        version: z.string().min(1).max(32),
        textHash: z.string().regex(/^[0-9a-f]{64}$/),
        accept: z.literal(true),
    })
        .nullable()
        .default(null),
})
    .refine((r) => !r.scopes.includes('clients.pii') || r.scopes.includes('clients.read'), {
    error: 'clients.pii выдаётся только вместе с clients.read',
    path: ['scopes'],
})
    .refine((r) => !r.scopes.includes('clients.pii') || r.allowedCidrs.length > 0 || r.acceptDynamicIpRisk, {
    error: 'для clients.pii нужен список адресов либо явный акцепт риска владельцем',
    path: ['allowedCidrs'],
});
/** Сузить права (4-В6): только убрать, не добавить. Добавление — новый акцепт. */
export const NarrowIntegrationAccessRequest = z
    .object({
    scopes: z.array(PublicApiScope).min(1),
    branchIds: z.array(Uuid).max(200),
})
    .refine((r) => !r.scopes.includes('clients.pii') || r.scopes.includes('clients.read'), {
    error: 'clients.pii выдаётся только вместе с clients.read',
    path: ['scopes'],
});
export const RevokeIntegrationAccessRequest = z.object({
    comment: z.string().trim().max(500).optional(),
});
/**
 * Состояние интеграции в «Кому открыт доступ» (4-В6):
 * no_access — заведена, доступа не выдавали; active — работает; suspended — отключена нами;
 * revoked — доступ отозван салоном; expired — истёк срок доступа.
 */
export const IntegrationState = z.enum(['no_access', 'active', 'suspended', 'revoked', 'expired']);
export const IntegrationAccessView = z.object({
    grantId: Uuid,
    scopes: z.array(PublicApiScope),
    /** Пусто — все филиалы, включая будущие. */
    branchIds: z.array(Uuid),
    grantedAt: Instant,
    /** Имя того, кто дал доступ, — снимком на момент чтения. */
    grantedBy: z.string().nullable(),
    /** Поручение: версия, цель, был ли второй фактор. null — прав на ПД не было. */
    instruction: z
        .object({
        version: z.string(),
        purpose: z.string().nullable(),
        twoFactorConfirmed: z.boolean(),
        acceptedAt: Instant,
    })
        .nullable(),
    validUntil: Instant.nullable(),
    revokedAt: Instant.nullable(),
    revokedBy: z.string().nullable(),
});
export const IntegrationView = z.object({
    id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    homepageUrl: z.string().nullable(),
    developer: z.object({ legalName: z.string(), inn: z.string() }),
    state: IntegrationState,
    /** Действующий доступ, а если его нет — последний отозванный или истёкший. */
    access: IntegrationAccessView.nullable(),
    /** «Выгружено карточек за 7 дней» — и сколько из них с открытыми контактами. */
    piiReads7d: z.object({
        cards: z.number().int().nonnegative(),
        unmasked: z.number().int().nonnegative(),
    }),
    activeKeys: z.number().int().nonnegative(),
    /** Самый поздний срок среди действующих ключей. */
    keysValidUntil: Instant.nullable(),
    createdAt: Instant,
});
export const IntegrationList = z.object({ items: z.array(IntegrationView) });
export const IntegrationAccessResult = z.object({
    integration: IntegrationView,
    issued: IssuedApiKey,
});
// ───────────────────────── подпись входящих запросов (режим signed) ─────────────────────────
/** Метка времени запроса в signed-режиме. Схема подписи идентична исходящим вебхукам. */
export const REQUEST_SIGNATURE_TIMESTAMP_HEADER = 'Planerius-Timestamp';
export const REQUEST_SIGNATURE_HEADER = 'Planerius-Signature';
/**
 * Окно ±300 с. Метка времени и есть защита от повтора: без её проверки
 * перехваченный когда-то запрос можно воспроизводить вечно.
 */
export const SIGNATURE_TOLERANCE_SECONDS = 300;
/**
 * Подписываемая строка: «<unix-секунды>.<СЫРОЕ тело>». Сырое — значит до разбора JSON:
 * любая нормализация (пробелы, порядок ключей, перекодировка) ломает подпись.
 */
export function signaturePayload(timestampSeconds, rawBody) {
    return `${timestampSeconds}.${rawBody}`;
}
// ───────────────────────── карточка клиента в публичном API ─────────────────────────
/**
 * Маскированное значение контакта: «+7 999 ***-**-12», «i***@mail.ru».
 * Требование хотя бы одной звёздочки — не украшение, а зацепка для обязательного теста
 * «в теле ответа без clients.pii нет ни одного открытого контакта» (ADR-036 §9).
 */
export const MaskedContact = z
    .string()
    .regex(/\*/, 'маскированный контакт обязан содержать *')
    .meta({ examples: ['+7 999 ***-**-12'] });
/**
 * Карточка клиента глазами стороннего приложения.
 *
 * Чего здесь нет и не будет ни под каким скоупом: заметки мастеров (свободный текст,
 * куда пишут аллергии и состояние кожи, — спецкатегория ПД, которую мы формально
 * не собираем), фото работ (opt-in ПД с возможными геометками) и текст причины
 * чёрного списка (личные оценочные суждения о человеке). Факт blacklisted отдаётся:
 * он влияет на возможность записи, то есть нужен интеграции для работы.
 */
export const PublicApiClient = z.object({
    id: Uuid,
    name: z.string(),
    /** Открытый E.164 только при clients.pii, иначе маска; null — телефона в карточке нет. */
    phone: z.union([Phone, MaskedContact]).nullable(),
    email: z.union([z.email(), MaskedContact]).nullable(),
    /** null без clients.pii: дата рождения — тоже ПД, а не «поле для поздравления». */
    birthDate: LocalDate.nullable(),
    /**
     * Признак того, что контакты замаскированы. Без него интегратор попробует
     * позвонить по маске и решит, что у нас битые данные.
     */
    contactsMasked: z.boolean(),
    tags: z.array(z.string()),
    blacklisted: z.boolean(),
    visitsCount: z.number().int().nonnegative(),
    noShowCount: z.number().int().nonnegative(),
    totalSpentMinor: MinorAmount,
    lastVisitAt: Instant.nullable(),
    createdAt: Instant,
    updatedAt: Instant,
});
// ───────────────────────── ресурсы чтения (ПА-3) ─────────────────────────
/** Страница списка: курсор непрозрачный, конец списка — nextCursor = null. */
const pageOf = (item) => z.object({ items: z.array(item), nextCursor: z.string().nullable() });
const Cursor = z.string().max(200);
const Limit = z.coerce.number().int().min(1).max(100).default(50);
/**
 * Запись глазами интеграции салона.
 *
 * Не AppointmentView витрины: там ссылки «отменить/перенести» (предъявительские токены,
 * GAPS E6) и сроки самообслуживания клиента — интеграции они не нужны. Здесь есть то,
 * чего нет у витрины: чья это запись (клиент — ссылкой и именем). Комментария клиента
 * нет: в нём пишут что угодно, включая сведения о здоровье, а поручение перечисляет
 * «имя клиента, время, услуга, мастер, сумма» — и ничего сверх.
 */
export const PublicApiAppointment = z.object({
    id: Uuid,
    status: AppointmentStatus,
    startAt: Instant,
    endAt: Instant,
    timezone: TimeZone,
    branch: AppointmentBranchView,
    employee: AppointmentEmployeeView.nullable(),
    services: z.array(AppointmentServiceView),
    priceMinor: MinorAmount,
    prepaidMinor: MinorAmount,
    source: BookingSource,
    client: z.object({ id: Uuid, name: z.string() }),
    createdAt: Instant,
    updatedAt: Instant,
});
/** Окно выборки записей — не больше 31 суток: месяц журнала за запрос, не год. */
export const PUBLIC_APPOINTMENTS_MAX_WINDOW_DAYS = 31;
export const ListPublicAppointmentsQuery = z
    .object({
    from: Instant,
    to: Instant,
    branchId: Uuid.optional(),
    status: AppointmentStatus.optional(),
    cursor: Cursor.optional(),
    limit: Limit,
})
    .refine((q) => Date.parse(q.to) > Date.parse(q.from), { error: 'to должен быть позже from', path: ['to'] })
    .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= PUBLIC_APPOINTMENTS_MAX_WINDOW_DAYS * 86_400_000, { error: `окно выборки — не больше ${PUBLIC_APPOINTMENTS_MAX_WINDOW_DAYS} суток`, path: ['to'] });
export const PublicApiAppointmentPage = pageOf(PublicApiAppointment);
/** Клиенты — по времени изменения: интеграция синхронизирует «что поменялось с прошлого раза». */
export const ListPublicClientsQuery = z.object({
    updatedSince: Instant.optional(),
    cursor: Cursor.optional(),
    limit: Limit,
});
export const PublicApiClientPage = pageOf(PublicApiClient);
export const PublicApiResourceId = z.object({ id: Uuid });
/** Каталог салона в пределах филиалов ключа: то, на что ссылаются записи. */
export const PublicApiCatalog = z.object({
    branches: z.array(z.object({ id: Uuid, name: z.string(), timezone: TimeZone, address: z.string().nullable() })),
    categories: z.array(z.object({ id: Uuid, parentId: Uuid.nullable(), name: z.string() })),
    services: z.array(z.object({
        id: Uuid,
        categoryId: Uuid.nullable(),
        /** null — услуга всего салона, а не одного филиала. */
        branchId: Uuid.nullable(),
        name: z.string(),
        durationMinutes: z.number().int().positive(),
        priceMinor: MinorAmount,
        isOnlineBookable: z.boolean(),
    })),
    employees: z.array(z.object({
        id: Uuid,
        publicName: z.string(),
        positionName: z.string().nullable(),
        isBookable: z.boolean(),
    })),
    /**
     * Причины отмены салона: код уходит в POST /appointments/{id}/cancel. Код, а не текст:
     * именно он решает, считать ли отмену неявкой и сгорает ли предоплата.
     */
    cancelReasons: z.array(z.object({ code: z.string(), title: z.string(), actor: CancellationActor })),
});
// ───────────────────────── запись через API (ПА-7) ─────────────────────────
//
// Решения владельца 12.10.2026: запись — только существующему клиенту салона (согласия
// у него собраны салоном; новых клиентов через API — позже); интеграция действует как
// администратор (онлайн-окно и сроки отмены не действуют, запрещена только двойная
// занятость мастера); клиент уведомляется по умолчанию, интеграция может выключить.
export const CreatePublicAppointmentRequest = z.object({
    branchId: Uuid,
    /** Пусто — «неразобранная» запись: мастера назначит салон. */
    employeeId: Uuid.optional(),
    startAt: Instant,
    serviceIds: z.array(Uuid).min(1).max(10),
    /** Карточка, которая уже есть в салоне и видна ключу. Новых клиентов через API нет. */
    clientId: Uuid,
    comment: z.string().trim().max(1000).optional(),
    notifyClient: z.boolean().default(true),
});
/**
 * Перенос через API — всегда действие салона: интеграция действует как администратор,
 * и онлайн-сроки переноса клиента к ней не применяются. Поэтому инициатора здесь нет —
 * в событии appointment.rescheduled он всегда staff.
 */
export const ReschedulePublicAppointmentRequest = z.object({
    startAt: Instant,
    employeeId: Uuid.optional(),
    comment: z.string().trim().max(1000).optional(),
    notifyClient: z.boolean().default(true),
    /** updatedAt записи из ответа API: защита от чужой правки между чтением и переносом. */
    expectedUpdatedAt: Instant.optional(),
});
export const CancelPublicAppointmentRequest = z.object({
    initiatedBy: CancellationActor.default('staff'),
    /** Код из GET /catalog → cancelReasons. */
    reasonCode: z.string().min(1).max(64),
    comment: z.string().trim().max(1000).optional(),
    notifyClient: z.boolean().default(true),
    expectedUpdatedAt: Instant.optional(),
});
// ───────────────── кабинет: журнал обращений интеграции (4-В6, вопрос 41) ─────────────────
export const IntegrationRequestLogQuery = z.object({
    /** errors — только неудачные (4xx/5xx): «почему у нас не работает интеграция». */
    only: z.enum(['all', 'errors']).default('all'),
    cursor: Cursor.optional(),
    limit: Limit,
});
export const IntegrationRequestEntry = z.object({
    at: Instant,
    method: z.string(),
    /** Шаблон пути, а не путь: идентификаторы клиентов в журнал не попадают. */
    route: z.string(),
    statusCode: z.number().int(),
    problemCode: z.string().nullable(),
    limitScope: z.string().nullable(),
    durationMs: z.number().int().nonnegative(),
    kid: z.string().nullable(),
    requestId: z.string(),
    ip: z.string().nullable(),
    replay: z.boolean(),
});
export const IntegrationRequestLog = z.object({
    items: z.array(IntegrationRequestEntry),
    nextCursor: z.string().nullable(),
    /** Сводка за сутки — шапка журнала: сколько, сколько неудачных, сколько упёрлось в лимит. */
    last24h: z.object({
        requests: z.number().int().nonnegative(),
        errors: z.number().int().nonnegative(),
        rateLimited: z.number().int().nonnegative(),
    }),
    /** Журнал хранится 30 суток — экран говорит это прямо. */
    retentionDays: z.number().int().positive(),
});
// ───────────────────────── ошибки публичного API ─────────────────────────
/**
 * Коды отказов инфраструктуры публичного API. Доменные отказы записи
 * (slot_taken, client_blacklisted и прочие) живут в ./errors.ts и здесь не дублируются:
 * запись через публичное API проходит тот же домен, что и через витрину,
 * а два перечня одних и тех же кодов гарантированно разъедутся.
 */
export const PublicApiErrorCode = z.enum([
    // 400
    'idempotency_key_required',
    'malformed_request',
    // 401 — субъект не аутентифицирован. Коды разные, потому что действия интегратора
    // разные: «вставь ключ», «выпусти новый», «ты перепутал окружения», «почини часы».
    'missing_credentials',
    'invalid_key',
    'key_expired',
    'key_revoked',
    'key_env_mismatch',
    'signature_invalid',
    // 403 — аутентифицирован, но не вправе.
    'insufficient_scope',
    'ip_not_allowed',
    'grant_revoked',
    'tenant_suspended',
    // 404 — в том числе объект чужого салона. Именно 404, а не 403: иначе перебор
    // идентификаторов превращается в подтверждение существования чужих записей.
    'not_found',
    // 409
    'idempotency_in_progress',
    // 410
    'api_version_sunset',
    'endpoint_removed',
    // 422
    'idempotency_key_reused',
    // 429
    'rate_limited',
    'too_many_concurrent',
    'daily_quota_exceeded',
    'temporarily_blocked',
    // 501 — путь /v1/oauth/token зарезервирован под этап 5, чтобы его не занял никто другой.
    'oauth_not_available',
]);
/** Код → HTTP-статус. Изменение этой таблицы — ЛОМАЮЩЕЕ изменение (ADR-036 §3.1). */
export const PUBLIC_API_ERROR_STATUS = {
    idempotency_key_required: 400,
    malformed_request: 400,
    missing_credentials: 401,
    invalid_key: 401,
    key_expired: 401,
    key_revoked: 401,
    key_env_mismatch: 401,
    signature_invalid: 401,
    insufficient_scope: 403,
    ip_not_allowed: 403,
    grant_revoked: 403,
    tenant_suspended: 403,
    not_found: 404,
    idempotency_in_progress: 409,
    api_version_sunset: 410,
    endpoint_removed: 410,
    idempotency_key_reused: 422,
    rate_limited: 429,
    too_many_concurrent: 429,
    daily_quota_exceeded: 429,
    temporarily_blocked: 429,
    oauth_not_available: 501,
};
/** Код → значение поля type. Дефисы, потому что это часть URL, а не идентификатор в коде. */
export function publicApiProblemTypeOf(code) {
    return `${PROBLEM_TYPE_PREFIX}${code.replaceAll('_', '-')}`;
}
/**
 * Какой именно рубеж лимита сработал.
 *
 * Обязателен в теле 429: без него интегратор не отличит «я слишком быстр» (замедлиться)
 * от «соседняя интеграция салона ест общий лимит» (звонить салону) и будет ретраить вечно.
 */
export const RateLimitScope = z.enum(['key', 'tenant', 'application', 'concurrency', 'daily_quota']);
/**
 * Тело ошибки публичного API: RFC 9457 плюс машинный код и ровно тот контекст,
 * который нужен чужому коду для правильного следующего действия.
 *
 * Чего здесь нет никогда: текстов и кодов ошибок PostgreSQL (в них имена таблиц
 * и чужие данные), стектрейсов, traceparent, имён узлов и версий ПО (ADR-036 §9).
 */
export const publicApiProblemDetails = problemDetails.extend({
    code: PublicApiErrorCode,
    /** Отдаём всегда: без идентификатора запроса поддержка разбирает обращение вслепую. */
    requestId: z.string(),
    /** insufficient_scope: чего не хватило. Дублирует WWW-Authenticate для тех, кто читает тело. */
    requiredScope: PublicApiScope.optional(),
    /** rate_limited и родственные. */
    limitScope: RateLimitScope.optional(),
    retryAfterSeconds: z.number().int().nonnegative().optional(),
    /** key_expired: когда истёк — чтобы интегратор понял, что чинить ключ, а не код. */
    expiredAt: Instant.optional(),
    /** api_version_sunset: когда версия отключена и куда мигрировать. */
    sunsetAt: Instant.optional(),
    migrationGuideUrl: z.url().optional(),
    /** idempotency_*: тот самый ключ, по которому произошёл конфликт. */
    idempotencyKey: PublicApiIdempotencyKey.optional(),
});
/**
 * Значение WWW-Authenticate при нехватке скоупа. Интегратор должен узнать,
 * чего не хватает, из ответа, а не из переписки с поддержкой.
 */
export function insufficientScopeChallenge(scope) {
    return `Bearer error="insufficient_scope", scope="${scope}"`;
}
// ───────────────────────── песочница ─────────────────────────
/**
 * Перехваченное сообщение песочницы. Отправка SMS, e-mail, Telegram/MAX и пушей
 * в sandbox-тенанте технически невозможна — перехватывается на уровне канала
 * доставки (ADR-017) и складывается сюда: разработчик видит, что «ушло бы»,
 * и проверяет текст, не будя ничьего реального клиента в три часа ночи.
 */
export const SandboxMessage = z.object({
    id: Uuid,
    channel: z.enum(['sms', 'email', 'telegram', 'max', 'push']),
    to: z.string(),
    subject: z.string().nullable(),
    body: z.string(),
    templateCode: z.string().nullable(),
    createdAt: Instant,
});
export const CreateSandboxRequest = z.object({
    applicationId: Uuid,
    /** Сид ставит 2 филиала в РАЗНЫХ часовых поясах: это ловит половину ошибок со временем. */
    profile: z.enum(['barbershop', 'beauty']).default('barbershop'),
});
export const SandboxView = z.object({
    tenantSlug: TenantSlug,
    applicationId: Uuid,
    createdAt: Instant,
    /**
     * Песочница очищается автоматически раз в 30 дней. Это защита от главного риска
     * песочницы — «залью туда боевую базу салона, чтобы протестировать на реальных данных».
     */
    purgeAt: Instant,
});
//# sourceMappingURL=public-api.js.map